import { Info } from "lucide-react";
import { formatDateTime, relativeTime } from "../lib/api";

interface AuditInfoProps {
  /** Fecha de creación del registro (ISO string). */
  createdAt: string;
  /**
   * Fecha de última modificación. Si es null/undefined o coincide con
   * createdAt, el componente entiende que el registro nunca se editó.
   */
  updatedAt?: string | null;
  /** Tamaño del icono. Default 12px. */
  size?: "xs" | "sm";
}

const ICON_CLS: Record<NonNullable<AuditInfoProps["size"]>, string> = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
};

/**
 * Pista inline de auditoría — pequeño icono Info que en hover muestra
 * "creado / modificado" + fecha relativa + fecha absoluta. Usa el
 * `title` HTML nativo (accesible en hover/focus, anunciado por
 * lectores de pantalla). El usuario que necesita auditoría completa
 * va a /auditoria, pero la mayoría sólo quiere saber "es reciente?".
 *
 * Por ahora muestra sólo fechas (no quién editó) — para resolver el
 * actor habría que cruzar con audit_log, costo > beneficio v1.
 */
export function AuditInfo({
  createdAt,
  updatedAt,
  size = "xs",
}: AuditInfoProps): JSX.Element {
  const fueModificado = !!updatedAt && updatedAt !== createdAt;
  const last = fueModificado ? updatedAt! : createdAt;
  const verbo = fueModificado ? "Modificado" : "Creado";
  const relativo = relativeTime(last);
  const absoluto = formatDateTime(last);

  // El title combina ambas líneas — los browsers lo muestran multilínea.
  const tooltip = fueModificado
    ? `Modificado ${relativo} · ${absoluto}\nCreado ${formatDateTime(createdAt)}`
    : `Creado ${relativo} · ${absoluto}`;

  return (
    <span
      className="inline-flex items-center text-neutral-600 hover:text-neutral-700 transition-colors cursor-help"
      title={tooltip}
      aria-label={`${verbo} ${relativo}`}
    >
      <Info className={ICON_CLS[size]} />
    </span>
  );
}
